import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Drawer,
  Flex,
  Grid,
  Loader,
  LoadingOverlay,
  Menu,
  Modal,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { useDebouncedState, useDisclosure, useHotkeys } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import {
  IconArrowBackUp,
  IconEdit,
  IconFileTypeCsv,
  IconListCheck,
  IconListDetails,
  IconPlus,
  IconSearch,
  IconTrash,
} from "@tabler/icons-react";
import {
  MantineReactTable,
  MRT_ColumnDef,
  MRT_RowSelectionState,
  useMantineReactTable,
} from "mantine-react-table";
import React, { useDeferredValue, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import _breadcrumb from "../../_base/component/_layout/_breadcrumb";
import { getDataFAQSelect } from "../../api/ApiConfigWeb";
import { _localization } from "../../config/location";
import DeleteFAQ from "./DeleteFAQ";
import DetailLineFAQs from "./DetailLineFAQs";
import CreateFAQ from "./CreateFAQ";
import EditFAQ from "./EditFAQ";
import { BASE_API_URL } from "../../config";
import { NotificationExtension } from "../../_base/extension/NotificationExtension";

const FAQ = () => {
  const headerRef = React.useRef<HTMLDivElement>(null);
  const [data, setData] = useState<any[]>([]);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [height, setHeight] = useState(0);
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 20,
  });
  const rowSelectionDeferred =
    useDeferredValue<MRT_RowSelectionState>(rowSelection);
  const [selectIds, setSelectIds] = useState<string[]>([]);
  const [visible, { toggle, open, close }] = useDisclosure(false);
  const [opened, { open: openCreate, close: closeCreate }] =
    useDisclosure(false);
  const [openedEdit, { open: openEdit, close: closeEdit }] =
    useDisclosure(false);
  const [openedDetail, { open: openDetail, close: closeDetail }] =
    useDisclosure(false);
  const [urlSelect, setUrlSelect] = useState<string>("");
  const [detailData, setDetailData] = useState<any>(null);
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const _searchJson = sessionStorage.getItem(String(location.pathname))
    ? JSON.parse(sessionStorage.getItem(String(location.pathname)) ?? "")
    : null;
  const checkedPinSearch = localStorage.getItem("checkedPinSearch") == "true";
  const [search, setSearch] = useState<any>({});
  const [keySearch, setKeySearch] = useDebouncedState<string>(
    _searchJson && checkedPinSearch ? _searchJson?.keySearch : "",
    300
  );

  const columns = React.useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        accessorKey: "STT",
        header: "STT",
        size: 50,
        Cell: ({ row }) => (
          <Text fw={500} size="16px">
            {row.index + 1}
          </Text>
        ),
      },
      {
        accessorKey: "url",
        header: "Đường dẫn URL",
        size: 300,
      },
      {
        accessorKey: "title",
        header: "Tiêu đề",
        size: 300,
      },
      {
        accessorKey: "action",
        header: "Thao tác",
        enableSorting: false,
        enableColumnFilter: false,
        size: 10,
        Cell: ({ row }) => (
          <Flex justify={"start"} align={"center"} gap={"xs"}>
            <Tooltip label="Chi tiết">
              <ActionIcon
                variant="light"
                color="blue"
                onClick={() => {
                  setDetailData(row.original.faqs);
                  openDetail();
                }}
              >
                <IconListDetails size={16} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Chỉnh sửa">
              <ActionIcon
                variant="light"
                color="yellow"
                onClick={() => {
                  openEdit();
                  setUrlSelect(row.original.url);
                }}
              >
                <IconEdit size={16} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Xóa">
              <ActionIcon
                variant="light"
                color="red"
                onClick={() => modalsDeleteFAQToUrl([row.original.url])}
              >
                <IconTrash size={16} />
              </ActionIcon>
            </Tooltip>
          </Flex>
        ),
      },
    ],
    []
  );

  const modalsDeleteFAQToUrl = (listUrl: string[]) => {
    modals.openConfirmModal({
      title: <Title order={5}>Xóa FAQ</Title>,
      size: "auto",
      children: <DeleteFAQ listUrl={listUrl} onSuccess={fetchData} />,
      confirmProps: { display: "none" },
      cancelProps: { display: "none" },
    });
  };

  const fetchData = async (onResert?: boolean) => {
    setIsLoading(true);
    setIsRefetching(true);
    setIsError(false);
    try {
      let url = `?id=${id}`;

      const params = new URLSearchParams();
      Object.entries(search).forEach(([key, value]) => {
        if (Array.isArray(value) && value.length > 0) {
          value.forEach((item) => params.append(key, String(item)));
        } else if (typeof value === "string" && value.length > 0) {
          params.append(key, value);
        }
      });

      // Add KeySearch if provided
      if (keySearch.length > 0) {
        params.append("KeySearch", keySearch.trim());
      }

      // Add pagination parameters (skip/take)
      const skip = pagination.pageIndex * pagination.pageSize;
      const take = pagination.pageSize;
      params.append("skip", String(skip));
      params.append("take", String(take));

      url += `&${params.toString()}`;

      const res = await getDataFAQSelect(url);

      if (res && res.success) {
        const result = res.data;
        setData(result);
        setRowCount(res.totalCount);
        if (onResert) {
          table.resetRowSelection();
        }
      } else {
        setData([]);
      }
    } catch (error) {
      setData([]);
      setIsError(true);
      console.error(error);
      return;
    } finally {
      setIsLoading(false);
      setIsRefetching(false);
      sessionStorage.setItem(
        String(location.pathname),
        JSON.stringify({
          ...search,
          keySearch: keySearch,
        })
      );
    }
  };

  const handleExportData = async (type: string) => {
    open();
    try {
      let url = `/ExportExcel/export-faq?`;

      if (type === "all") {
        url += `categoryIds=${id}`;
      } else {
        url += selectIds.map((item) => `urls=${item}`).join("&");
      }

      const _url = BASE_API_URL + url;
      const token = localStorage.getItem("token");

      const response = await fetch(_url, {
        method: "GET",
        headers: {
          "Content-Type": "application/vnd.ms-excel",
          Authorization: `Bearer ${token?.replace(`"`, "").replace(`"`, "")}`,
        },
      });

      if (!response.ok) {
        NotificationExtension.Fails("Export Excel thất bại !");
      } else {
        NotificationExtension.Success("Export Excel thành công !");
        const blob = await response.blob();
        const urlss = window.URL.createObjectURL(blob);
        const _name = new Date().toISOString().slice(0, 10);
        let filename = "danh_sach_faqs" + _name + ".xlsx";
        const link = document.createElement("a");
        link.href = urlss;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.log(error);
    } finally {
      close();
    }
  };

  useEffect(() => {
    fetchData();
  }, [pagination?.pageIndex, pagination?.pageSize]);

  useEffect(() => {
    //   startTransition(() => {
    const valuesList = Object.keys(rowSelectionDeferred);
    setSelectIds(valuesList);
    if (valuesList.length < 1) setSelectIds([]);
    else {
      const valuesList = Object.keys(rowSelectionDeferred);
      setSelectIds(valuesList);
    }
    //  });
  }, [rowSelectionDeferred]);

  useEffect(() => {
    const headerHeight = headerRef.current?.offsetHeight || 0;
    const handleResize = () => {
      setHeight(window.innerHeight - (150 + headerHeight));
    };

    handleResize(); // Set initial height
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  console.log(data, "ádasdasdasd");
  const table = useMantineReactTable({
    columns,
    data,
    enableColumnResizing: false,
    positionToolbarAlertBanner: "bottom",
    //add custom action buttons to top-left of top toolbar
    enableTopToolbar: false,
    mantineTopToolbarProps: {
      style: {
        borderBottom: "3px solid rgba(128, 128, 128, 0.5)",
        marginBottom: 5,
      },
    },
    localization: _localization,
    enableRowSelection: true,
    getRowId: (row) => row.url?.toString(),
    initialState: {
      showColumnFilters: false,
      columnPinning: {
        left: ["mrt-row-select"],
      },
      columnVisibility: { id: true, phone: false, fax: false },
      density: "xs",
    },
    mantineTableContainerProps: {
      style: { maxHeight: height, minHeight: height },
    },
    enableStickyHeader: true,
    enableExpandAll: false,
    manualPagination: true,
    onRowSelectionChange: setRowSelection,
    rowCount: rowCount,
    onPaginationChange: setPagination,
    mantineTableBodyCellProps: ({ row }) => ({
      style: {
        fontWeight: "500", //conditional styling
        fontSize: "12.5px",
        padding: "5px 15px",
      },
    }),
    state: {
      isLoading,
      pagination: pagination,
      showAlertBanner: isError,
      showProgressBars: isRefetching,
      showSkeletons: isLoading, //loading for the first time with no data
      rowSelection,
    },
    mantineToolbarAlertBannerProps: isError
      ? { color: "red", children: "Lỗi tải dữ liệu !" }
      : undefined,
    mantinePaginationProps: {
      showRowsPerPage: true,
      withEdges: true,
      rowsPerPageOptions: ["20", "50", "100"],
    },
    paginationDisplayMode: "pages",
    enableColumnPinning: true,
    mantineTableProps: {
      striped: false,
    },
    columnFilterDisplayMode: "popover",
    mantineTableBodyRowProps: ({ row }) => ({
      style: { cursor: "pointer" },
    }),
  });

  useHotkeys([
    [
      "Enter",
      () => {
        fetchData();
      },
    ],
  ]);

  return (
    <>
      <div
        ref={headerRef}
        style={{
          width: "100%",
        }}
      >
        <LoadingOverlay
          visible={visible}
          zIndex={1000}
          overlayProps={{ radius: "sm", blur: 5 }}
          loaderProps={{
            children: (
              <Flex direction={"column"} align="center" gap="xs">
                <Loader type="dots" size={35}></Loader>
                {/* <Title size={15}> Đang upload tài liệu...</Title> */}
                <Title size={15}> Đang xử lý....</Title>
              </Flex>
            ),
          }}
        />
        <Flex justify={"space-between"} align={"center"} w={"100%"}>
          <_breadcrumb></_breadcrumb>
          <Flex gap={"md"}>
            <Button
              leftSection={<IconArrowBackUp size={16} />}
              color="red"
              onClick={() => navigate(-1)}
            >
              Quay lại
            </Button>
            <Button
              leftSection={<IconTrash size={16} />}
              color="red"
              disabled={selectIds.length === 0}
              onClick={() => modalsDeleteFAQToUrl(selectIds)}
            >
              Xóa FAQ theo URL
            </Button>
            <Button
              leftSection={<IconPlus size={16} />}
              color="green"
              onClick={() => openCreate()}
            >
              Tạo mới
            </Button>
            <Menu>
              <Menu.Target>
                <Button leftSection={<IconFileTypeCsv size={16} />}>
                  Export Excel
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item
                  leftSection={<IconListCheck size={16} />}
                  onClick={() => handleExportData("all")}
                >
                  Export tất cả
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconListDetails size={16} />}
                  onClick={() => handleExportData("url")}
                >
                  Export các URL đã chọn
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Flex>
        </Flex>
        <Grid mt={10} w={"100%"}>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <TextInput
              placeholder="Nhập từ khóa tìm kiếm"
              defaultValue={keySearch}
              onChange={(e) => setKeySearch(e.currentTarget.value)}
              leftSection={<IconSearch color="#15aabf" />}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  fetchData();
                }
              }}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <Button
              onClick={() => fetchData()}
              leftSection={<IconSearch size={14} />}
            >
              Tìm kiếm
            </Button>
          </Grid.Col>
        </Grid>
      </div>
      <Box mt={10}>
        <Drawer.Root
          opened={opened}
          onClose={closeCreate}
          offset={8}
          size={"xl"}
          radius="md"
        >
          <Drawer.Overlay />
          <Drawer.Content>
            <Drawer.Header>
              <Drawer.Title>
                <Badge>Tạo câu hỏi</Badge>
              </Drawer.Title>
              <Drawer.CloseButton />
            </Drawer.Header>
            <Drawer.Body>
          <CreateFAQ
            id={Number(id)}
            onSuccess={fetchData}
            onClose={closeCreate}
          />
            </Drawer.Body>
          </Drawer.Content>
        </Drawer.Root>
        <Drawer.Root
          opened={openedEdit}
          onClose={closeEdit}
          offset={8}
          size={"xl"}
          radius="md"
        >
          <Drawer.Overlay />
          <Drawer.Content>
            <Drawer.Header>
              <Drawer.Title>
                <Badge>Chỉnh sửa câu hỏi</Badge>
              </Drawer.Title>
              <Drawer.CloseButton />
            </Drawer.Header>
            <Drawer.Body>
          <EditFAQ
            id={Number(id)}
            url={urlSelect}
            onSuccess={fetchData}
            onClose={closeEdit}
          />
            </Drawer.Body>
          </Drawer.Content>
        </Drawer.Root>
        <Modal
          opened={openedDetail}
          onClose={closeDetail}
          title={<Title order={3}>Chi tiết FAQ</Title>}
          size="xl"
          centered
          radius="md"
        >
          <DetailLineFAQs dataDetail={detailData} onSuccess={fetchData} />
        </Modal>
        <MantineReactTable table={table} />
      </Box>
    </>
  );
};

export default FAQ;
